import { Typography } from '@mui/material';
import {
  compareColorPct,
  formatCompareDiff,
  formatComparePct,
} from 'src/components/data-grid/data-grid-performance-split-business-overview';
import { CompareKind } from 'src/components/data-grid/compare-kind';

type InlineEvolutionProps = {
  value: number | null | undefined;
  compare: number | null | undefined;
  kind: CompareKind;
  inverted: boolean;
  evolutionMode: boolean;
  currency?: string;
  locale?: string;
};

const isMissing = (v: unknown) => v === null || v === undefined;

/**
 * Evolution vs. the compare period, appended to a grid cell (same look as Explore).
 * Rates always read in percentage points; volumes/costs read in % or in absolute delta
 * depending on the "% evolution" toggle.
 */
export function InlineEvolution({ value, compare, kind, inverted, evolutionMode, currency, locale }: InlineEvolutionProps) {
  // No compare row, or no current value (e.g. a NULL ratio): nothing meaningful to show.
  if (isMissing(compare) || isMissing(value)) return null;

  let text: string;
  if (kind === 'percent') text = formatCompareDiff(value, compare, 'percent');
  else if (evolutionMode) text = formatComparePct(value, compare);
  else text = formatCompareDiff(value, compare, kind, { currency, locale });

  return (
    <Typography
      sx={{
        width: 56,
        flexShrink: 0,
        textAlign: 'right',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color: compareColorPct(value ?? 0, compare, inverted),
      }}
    >
      {text}
    </Typography>
  );
}

/** One data set loaded in the grid and the per-field maxes computed on it (lazily, once per field). */
type PageScale = { rows: any[]; maxes: Record<string, number> };

/** Data set of each row object, so a cell can find its scale from `params.row` without scanning the grid. */
const scaleByRow = new WeakMap<object, PageScale>();

/** Registers the rows currently loaded in the grid; call once per data set (not per cell). */
export const registerPageRows = (rows: any[]): void => {
  const scale: PageScale = { rows, maxes: {} };
  rows.forEach((row) => {
    if (row && typeof row === 'object') scaleByRow.set(row, scale);
  });
};

/** Max of a numeric field over the data set of `row` (scales the in-cell bars); 0 for an unregistered row. */
export const pageMax = (row: any, field: string): number => {
  const scale = row && typeof row === 'object' ? scaleByRow.get(row) : undefined;
  if (!scale) return 0;
  if (!(field in scale.maxes)) {
    scale.maxes[field] = scale.rows.reduce((max, r) => Math.max(max, Number(r?.[field]) || 0), 0);
  }
  return scale.maxes[field];
};
